import { useBulkPayments } from "@entities/financeApproval/lib/hooks/useBulkPayment";
import { useSendBulkAmountOTP } from "@entities/financeApproval/lib/hooks/useSendBulkAmountOTP";
import { ToastService } from "@shared/lib/services";
import { IGstInvoice } from "@shared/lib/types/finance";
import { Button } from "@shared/ui/Button";
import { TextField } from "@shared/ui/TextField";
import { Flex, Typography } from "antd";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

const { Text } = Typography;

interface Props {
  onClose: () => void;
  influencers: IGstInvoice[];
  selectedbaseCostAggregate: number;
  selectedGstAmountAggregate: number;
}

export function BulkGstPaymentConfirmation({
  onClose,
  influencers,
  selectedbaseCostAggregate,
  selectedGstAmountAggregate,
}: Props) {
  const [token, setToken] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [countdown, setCountdown] = useState<number>(0);

  const {
    bulkPayments,
    isBulkPaymentsLoading,
    isBulkPaymentsSuccess,
    variables,
  } = useBulkPayments();

  const {
    sendBulkAmountOTP,
    isSendingBulkAmountOTPLoading,
    isSendingBulkAmountOTPSuccess,
  } = useSendBulkAmountOTP((token: string) => {
    setToken(token);
  });

  const sendOtp = () => {
    sendBulkAmountOTP("");
    setCountdown(90); // 90 seconds countdown
  };

  const handleResendOtp = () => {
    if (countdown === 0) {
      sendOtp();
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleSubmit = () => {
    if (otp.length !== 6) {
      ToastService.error("Please enter a valid 6-digit OTP");
      return;
    }

    bulkPayments({
      vendorPaymentsRequests: influencers.map((row) => ({
        campaignInfluencerId: row.campaignInfluencerId,
        amount: row.invoiceFinanceFlags?.gstPayout?.left || 0,
        linkedInvoiceId: row._id,
        subType: "Gst",
      })),
      token: token,
      otp: otp,
    });
  };

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [countdown]);

  useEffect(() => {
    if (isSendingBulkAmountOTPSuccess) {
      ToastService.success("OTP sent successfully");
    }
  }, [isSendingBulkAmountOTPSuccess]);

  useEffect(() => {
    if (isBulkPaymentsSuccess) {
      ToastService.success("Bulk payments submitted successfully");
      onClose();
    }
  }, [isBulkPaymentsSuccess]);

  useEffect(() => {
    sendOtp();
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Text className={styles.question}>
          Are you sure you want to submit bulk payment?
        </Text>

        <div className={styles.amountDetails}>
          <div className={styles.amountRow}>
            <Text className={styles.amountLabel}>Base Amount:</Text>
            <Text className={styles.amountValue}>
              ₹{selectedbaseCostAggregate.toFixed(2)}
            </Text>
          </div>
          <div className={styles.amountRow}>
            <Text className={styles.amountLabel}>GST Amount:</Text>
            <Text className={styles.amountValue}>
              ₹{selectedGstAmountAggregate.toFixed(2)}
            </Text>
          </div>
        </div>

        <div className={styles.otpSection}>
          <Text className={styles.otpLabel}>
            Enter 6-digit OTP sent to your phone number
          </Text>

          <TextField
            value={otp}
            onChange={handleOtpChange}
            placeholder=""
            maxLength={6}
            className={styles.otpInput}
            disabled={isBulkPaymentsLoading}
          />

          <div className={styles.resendSection}>
            {countdown > 0 ? (
              <Text className={styles.resendTimer}>
                Resend in {formatTime(countdown)}
              </Text>
            ) : (
              <Button
                type="link"
                onClick={handleResendOtp}
                loading={isSendingBulkAmountOTPLoading}
                className={styles.resendButton}
              >
                Resend OTP
              </Button>
            )}
          </div>
        </div>

        <Flex justify="end" gap={8} className={styles.buttonContainer}>
          <Button onClick={onClose} disabled={isBulkPaymentsLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isBulkPaymentsLoading}
            disabled={otp.length !== 6}
          >
            Submit
          </Button>
        </Flex>
      </div>
    </div>
  );
}
